import * as React from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";

import { cn } from "@src/lib/utils/cn";

type SeparatorProps = React.ComponentPropsWithoutRef<
  typeof SeparatorPrimitive.Root
> & {
  // Text centred on a horizontal rule, e.g. "or". Ignored when vertical.
  // A labelled rule is always decorative so the label stays readable.
  label?: React.ReactNode;
};

const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  SeparatorProps
>(
  (
    {
      className,
      orientation = "horizontal",
      decorative = true,
      label,
      ...props
    },
    ref,
  ) => {
    const hasLabel = label != null && label !== false && label !== "";
    if (hasLabel && orientation === "horizontal") {
      return (
        <SeparatorPrimitive.Root
          ref={ref}
          decorative
          orientation={orientation}
          className={cn(
            "flex w-full shrink-0 items-center gap-3 text-xs-v4 text-text-tertiary",
            "before:h-px before:flex-1 before:bg-border-primary before:content-['']",
            "after:h-px after:flex-1 after:bg-border-primary after:content-['']",
            className,
          )}
          {...props}
        >
          {label}
        </SeparatorPrimitive.Root>
      );
    }

    return (
      <SeparatorPrimitive.Root
        ref={ref}
        decorative={decorative}
        orientation={orientation}
        className={cn(
          "shrink-0 bg-border-primary",
          orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
          className,
        )}
        {...props}
      />
    );
  },
);
Separator.displayName = SeparatorPrimitive.Root.displayName;

export { Separator };
export type { SeparatorProps };
